import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { IItem } from '../types';
import PrismicLink from '@components/PrismicLink';
import React from 'react';
import { isFilled } from '@prismicio/client';
import { PrismicRichText } from '@components/PrismicRichText';
import { usePrismicTextAlignmentStyle } from '@hooks/prismic/usePrismicTextAlignmentStyle';
import cn from 'classnames';
import { usePrismicTitleStyle } from '@hooks/prismic/usePrismicTitleStyle';

interface ICardProps {
  item: IItem;
}

const Card: React.FC<ICardProps> = ({ item }) => {
  const titleStyle = usePrismicTitleStyle(item.titleStyle);
  const textAlignment = usePrismicTextAlignmentStyle(item.textAlignment);
  const ctaAlignment = (() => {
    switch (item.textAlignment) {
      case 'center':
        return 'tw-self-auto';
      case 'left':
        return 'tw-self-start';
      default:
        return '';
    }
  })();

  if (!isFilled.image(item.img)) {
    return null;
  }

  return (
    <>
      <div className="container">
        <div className="img">
          <ResponsiveImage
            src={item.img.url as string}
            alt={item.img.alt as string}
            objectFit="contain"
            layout="fill"
          />
        </div>
        <div className="text-block">
          <div className={cn(textAlignment, titleStyle)}>{item.title}</div>
          <div className="text">
            <PrismicRichText
              field={item.text}
              textAlignmentField={item.textAlignment}
            />
          </div>
        </div>
        {item.isCtaDisplayed && (
          <PrismicLink link={item.ctaLink}>
            <span
              className={cn('tw-lf-button tw-lf-button-primary', ctaAlignment)}
            >
              {item.ctaLabel}
            </span>
          </PrismicLink>
        )}
      </div>
      <style jsx>{`
        .container {
          border-radius: var(--rd-3);
          padding: ${item.bgColor
            ? 'var(--space-4) var(--space-5) var(--space-6)'
            : 'var(--space-5)'};
          background-color: ${item.bgColor || `transparent`};
          display: flex;
          align-items: center;
          flex-direction: ${!item.reverse ? `column` : `column-reverse`};
        }

        .container > * {
          margin-bottom: ${item.reverse ? `0` : `var(--space-4)`};
        }

        .container > *:last-child {
          margin-bottom: 0;
        }

        .text-block {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
          flex: 1;
        }

        .text {
          font-weight: var(--font-weight-light);
        }

        .text > :global(p) {
          font-size: var(--font-size-2);
          line-height: 24px;
        }

        .img {
          height: ${item?.img?.dimensions?.height || 0}px;
          width: 100%;
          position: relative;
        }
      `}</style>
    </>
  );
};

export default Card;
